/**
 * AuditLogDetailsModal
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Trash2, X } from 'lucide-react';

import type { AuditLog } from '@/hooks';
import { useDeleteAuditLog } from '@/hooks';
import { cn, getApiErrorMessage } from '@/lib/utils';
import { Button } from '@/components/ui';
import { Card, CardTitle } from '@/components/ui';
import { SiemInvestigationBar } from '@/features/siem/components/investigate';
import {
  extractSmswiserKillContextFromAudit,
  hasSmswiserKillInvestigationTarget,
} from '@/features/siem/utils/extract-smswiser-kill-context';
import { useOptionalDropTimelineLauncher } from '@/features/siem/components/drop-timeline';
import {
  extractDropTimelineContextFromAudit,
  hasDropTimelineFollowTarget,
} from '@/features/siem/utils/drop-timeline';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/providers/ToastProvider';

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(value));
}

function prettyJson(value: unknown): string {
  if (value === undefined) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export const AuditLogDetailsModal: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  log: AuditLog | null;
  overlayZClass?: string;
}> = ({ open, onOpenChange, log, overlayZClass = 'z-50' }) => {
  const dropTimelineLauncher = useOptionalDropTimelineLauncher();
  const { user, hasPermission } = useAuth();
  const canDelete = (user?.role || '').toLowerCase() === 'admin';
  const deleteLog = useDeleteAuditLog();
  const { success: toastSuccess, error: toastError } = useToast();
  const handleClose = () => onOpenChange(false);

  const deletePrompt = useMemo(() => {
    if (!log) return 'Delete this log? This cannot be undone.';
    const label = log.action ? `"${log.action}"` : 'this log';
    return `Delete ${label}? This cannot be undone.`;
  }, [log]);

  const killContext = useMemo(() => {
    if (!log) return {};
    return extractSmswiserKillContextFromAudit(
      log.action,
      log.requestBody ?? undefined,
      log.entityId,
    );
  }, [log]);

  const dropTimelineCtx = useMemo(() => {
    if (!log) return {};
    return extractDropTimelineContextFromAudit(
      log.action,
      log.requestBody ?? undefined,
      log.entityId,
      log.entityName,
    );
  }, [log]);

  const showKillInvestigation = Boolean(
    log && hasSmswiserKillInvestigationTarget(killContext),
  );

  const showFollowDrop =
    Boolean(
      dropTimelineLauncher &&
        log &&
        (log.action === 'drop.create' || log.action === 'drop.delete') &&
        hasDropTimelineFollowTarget(dropTimelineCtx) &&
        hasPermission('investigation.drop_timeline'),
    );

  const goFollowDrop = () => {
    if (!dropTimelineLauncher) return;
    dropTimelineLauncher.openDropTimeline({
      ...(dropTimelineCtx.uniqueId != null ? { uniqueId: dropTimelineCtx.uniqueId } : {}),
      ...(dropTimelineCtx.wiserDropId != null
        ? { wiserDropId: dropTimelineCtx.wiserDropId }
        : {}),
    });
    handleClose();
  };

  const handleDelete = async () => {
    if (!log) return;
    if (!window.confirm(deletePrompt)) return;

    try {
      await deleteLog.mutateAsync(log.id);
      toastSuccess('Log deleted', 'The audit log entry has been removed.');
      handleClose();
    } catch (error) {
      toastError('Delete failed', getApiErrorMessage(error, 'Failed to delete audit log'));
    }
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className={cn('fixed inset-0', overlayZClass)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/50" onClick={handleClose} aria-hidden="true" />

          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            className="absolute inset-0 flex items-center justify-center p-3 sm:p-4"
            role="dialog"
            aria-modal="true"
            aria-label="Audit log details"
          >
            <div className="w-full max-w-5xl max-h-[92vh]">
              <Card className="shadow-xl overflow-hidden">
                <div className="flex flex-col max-h-[92vh]">
                  <div className="sticky top-0 z-10 border-b bg-card">
                    <div className="flex items-start justify-between gap-3 px-6 py-4">
                      <div className="min-w-0">
                        <CardTitle>Audit Log Details</CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          {log?.action || '—'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {showFollowDrop ? (
                          <Button variant="outline" size="sm" onClick={goFollowDrop}>
                            Follow drop
                          </Button>
                        ) : null}
                        {canDelete ? (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={handleDelete}
                            disabled={!log || deleteLog.isPending}
                            aria-label="Delete log"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </Button>
                        ) : null}
                        <Button variant="outline" size="sm" onClick={handleClose} aria-label="Close">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="overflow-y-auto px-6 py-5 space-y-4">
                    {!log ? (
                      <p className="text-sm text-muted-foreground">No log loaded.</p>
                    ) : (
                      <>
                        {showKillInvestigation ? (
                          <SiemInvestigationBar
                            killContext={killContext}
                            userEmail={log.userEmail}
                            canOpenWorkspace={hasPermission('drops.view')}
                            canOpenUsers={hasPermission('users.view')}
                            canOpenAuditLogs={hasPermission('audit.view')}
                          />
                        ) : null}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-muted-foreground">ID</span>
                            <div className="font-mono break-words">{log.id}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Date</span>
                            <div>{formatDateTime(log.createdAt)}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">User</span>
                            <div className="break-words">
                              {log.userEmail || '—'} {log.userRole ? <span className="text-muted-foreground">({log.userRole})</span> : null}
                            </div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Result</span>
                            <div>{log.success ? 'Success' : 'Failed'}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Route / Entity</span>
                            <div className="font-mono break-words">
                              {log.entityName || log.entityId || log.entityType || '—'}
                            </div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">IP</span>
                            <div className="font-mono break-words">{log.ipAddress || '—'}</div>
                          </div>
                          <div className="md:col-span-2">
                            <span className="text-muted-foreground">User-Agent</span>
                            <div className="font-mono break-words">{log.userAgent || '—'}</div>
                          </div>
                          {log.errorMessage ? (
                            <div className="md:col-span-2">
                              <span className="text-muted-foreground">Error</span>
                              <div className="text-destructive break-words">{log.errorMessage}</div>
                            </div>
                          ) : null}
                        </div>

                        <div>
                          <p className="text-sm font-medium">Request body</p>
                          <pre className="mt-2 text-xs rounded-lg border bg-muted/20 p-3 overflow-x-auto whitespace-pre-wrap break-words font-mono">
                            {prettyJson(log.requestBody || {})}
                          </pre>
                        </div>

                        <div>
                          <p className="text-sm font-medium">Changes</p>
                          <pre className="mt-2 text-xs rounded-lg border bg-muted/20 p-3 overflow-x-auto whitespace-pre-wrap break-words font-mono">
                            {prettyJson(log.changes || {})}
                          </pre>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default AuditLogDetailsModal;

